import { Injectable } from '@angular/core';
import { BuilderNode, GridPosition, GridTemplate } from '../interfaces/builder-screen.models';

@Injectable({
  providedIn: 'root'
})
export class GridLayoutService {
  private readonly defaultTemplate: GridTemplate = {
    columns: 24,
    rows: 50, // Linhas dinâmicas, começa com 50
    gap: '16px'
  };

  /**
   * Calcula posições da grade para todos os nós usando rowSpan/colSpan existentes
   * Usa layout "um componente por linha" para construção de formulários
   */
  calculateGridPosition(nodes: BuilderNode[]): Map<string, GridPosition> {
    const positions = new Map<string, GridPosition>();
    
    // Ordena nós por ordem de criação (implícito da ordem do array)
    // Inclui TODOS os nós raiz (incluindo abas) para posicionamento da grade
    const sortedNodes = [...nodes].filter(n => !n.parentId); // Filtra apenas nós filhos

    let currentRow = 0;

    for (const node of sortedNodes) {
      const rowSpan = node.props.rowSpan || 1;
      const colSpan = node.props.colSpan || 24;

      // Força um componente por linha - sempre começa na coluna 0
      const position: GridPosition = {
        row: currentRow,
        col: 0, // Sempre começa na coluna 0 para layout de formulário
        rowSpan,
        colSpan: Math.min(colSpan, this.defaultTemplate.columns) // Garante que colSpan não exceda a grade
      };
      
      positions.set(node.id, position);
      
      // Move para próxima linha para próximo componente (um por linha)
      currentRow += rowSpan;
    }

    return positions;
  }

  /**
   * Calcula posições da grade para componentes filhos (ignora filtragem por parentId)
   * Usa layout "um componente por linha" para construção de formulários
   */
  calculateChildGridPosition(nodes: BuilderNode[]): Map<string, GridPosition> {
    const positions = new Map<string, GridPosition>();
    
    // Ordena nós por ordem de criação (implícito da ordem do array)
    // Para nós filhos, não filtra por parentId
    const sortedNodes = [...nodes];

    let currentRow = 0;

    for (const node of sortedNodes) {
      const rowSpan = node.props.rowSpan || 1;
      const colSpan = node.props.colSpan || 24;

      // Força um componente por linha - sempre começa na coluna 0
      const position: GridPosition = {
        row: currentRow,
        col: 0, // Sempre começa na coluna 0 para layout de formulário
        rowSpan,
        colSpan: Math.min(colSpan, this.defaultTemplate.columns) // Garante que colSpan não exceda a grade
      };
      
      positions.set(node.id, position);
      
      // Move para próxima linha para próximo componente (um por linha)
      currentRow += rowSpan;
    }

    return positions;
  }

  /**
   * Encontra próxima posição disponível para um componente
   */
  findNextAvailablePosition(rowSpan: number, colSpan: number, occupied?: Set<string>, startRow: number = 0, startCol: number = 0): GridPosition {
    const occupiedCells = occupied || new Set<string>();
    
    // Garante que colSpan não exceda as colunas da grade
    const actualColSpan = Math.min(colSpan, this.defaultTemplate.columns);
    
    for (let row = startRow; row < this.defaultTemplate.rows; row++) {
      for (let col = startCol; col <= this.defaultTemplate.columns - actualColSpan; col++) {
        const position: GridPosition = {
          row,
          col,
          rowSpan,
          colSpan: actualColSpan
        };

        if (!this.checkCollision(position, occupiedCells)) {
          return position;
        }
      }
      startCol = 0; // Reseta coluna para próxima linha
    }

    // Fallback: coloca no final
    return {
      row: startRow,
      col: 0,
      rowSpan,
      colSpan: actualColSpan
    };
  }

  /**
   * Check if a position collides with occupied cells
   */
  checkCollision(position: GridPosition, occupiedCells: Set<string>): boolean {
    for (let r = position.row; r < position.row + position.rowSpan; r++) {
      for (let c = position.col; c < position.col + position.colSpan; c++) {
        if (occupiedCells.has(`${r},${c}`)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Check if a position collides with existing nodes
   */
  checkCollisionWithNodes(position: GridPosition, nodes: BuilderNode[], excludeNodeId?: string): boolean {
    const positions = this.calculateGridPosition(nodes.filter(n => n.id !== excludeNodeId));
    const occupied = new Set<string>();
    
    for (const [nodeId, pos] of positions) {
      if (nodeId !== excludeNodeId) {
        this.markCellsAsOccupied(pos, occupied);
      }
    }
    
    return this.checkCollision(position, occupied);
  }

  /**
   * Mark cells as occupied in the grid
   */
  private markCellsAsOccupied(position: GridPosition, occupied: Set<string>): void {
    for (let r = position.row; r < position.row + position.rowSpan; r++) {
      for (let c = position.col; c < position.col + position.colSpan; c++) {
        occupied.add(`${r},${c}`);
      }
    }
  }

  /**
   * Optimize layout by removing gaps and compacting nodes
   */
  optimizeLayout(nodes: BuilderNode[]): BuilderNode[] {
    const positions = this.calculateGridPosition(nodes);
    
    // Ordena posições por linha, depois por coluna
    const sortedEntries = Array.from(positions.entries())
      .sort(([, a], [, b]) => {
        if (a.row !== b.row) return a.row - b.row;
        return a.col - b.col;
      });

    // Compacta verticalmente
    const optimized = [...nodes];
    let currentRow = 0;
    
    for (const [nodeId, position] of sortedEntries) {
      const nodeIndex = optimized.findIndex(n => n.id === nodeId);
      if (nodeIndex >= 0) {
        const node = optimized[nodeIndex];
        const updatedProps = {
          ...node.props,
          gridRow: currentRow,
          gridCol: position.col
        };
        
        optimized[nodeIndex] = {
          ...node,
          props: updatedProps
        };
        
        currentRow = Math.max(currentRow, currentRow + position.rowSpan);
      }
    }

    return optimized;
  }

  /**
   * Get current grid template configuration
   */
  getGridTemplate(): GridTemplate {
    return { ...this.defaultTemplate };
  }

  /**
   * Update grid gap
   */
  updateGridGap(gap: string): void {
    this.defaultTemplate.gap = gap;
  }

  /**
   * Calculate total grid height based on nodes
   */
  calculateGridHeight(nodes: BuilderNode[]): number {
    const positions = this.calculateGridPosition(nodes);
    let maxRow = 0;
    
    for (const position of positions.values()) {
      maxRow = Math.max(maxRow, position.row + position.rowSpan);
    }
    
          return Math.max(maxRow, 10); // Mínimo 10 linhas
  }

  /**
   * Calculate grid height for child components
   */
  calculateChildGridHeight(nodes: BuilderNode[]): number {
    const positions = this.calculateChildGridPosition(nodes);
    let maxRow = 0;
    
    for (const position of positions.values()) {
      maxRow = Math.max(maxRow, position.row + position.rowSpan);
    }
    
          return Math.max(maxRow, 3); // Mínimo 3 linhas para grades filhas
  }

  /**
   * Get grid cell coordinates as CSS Grid values
   */
  getGridCellCSS(position: GridPosition): { gridRow: string; gridColumn: string } {
    return {
      gridRow: `${position.row + 1} / ${position.row + position.rowSpan + 1}`,
      gridColumn: `${position.col + 1} / ${position.col + position.colSpan + 1}`
    };
  }

  /**
   * Validate if a position is within grid bounds
   */
  isValidPosition(position: GridPosition): boolean {
    return position.row >= 0 &&
           position.col >= 0 &&
           position.col + position.colSpan <= this.defaultTemplate.columns &&
           position.rowSpan > 0 &&
           position.colSpan > 0;
  }
}
